import { parseWalletImport, WALLET_PROMPT, type WalletImport } from '@trips/shared/wallet-import';
import { useState } from 'react';
import { put, remove } from '../../data/mutate';
import { selectToday, selectWallet } from '../../data/selectors';
import { useStore } from '../../data/store';
import { copyText } from '../../lib/platform';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { TextArea, Toggle } from '../../ui/Field';
import { Page } from '../../ui/Page';
import { toast } from '../../ui/Toast';
import s from './SettingsScreen.module.css';

/** Brings points, cards, perks and redemptions over from what Claude knows (claude.ai). */
export function WalletImportPage() {
  const today = useStore(selectToday);
  const existing = useStore(selectWallet);
  const [text, setText] = useState('');
  const [replace, setReplace] = useState(true);
  const [result, setResult] = useState<WalletImport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = (v: string) => {
    setText(v);
    setError(null);
    if (!v.trim()) return setResult(null);
    try {
      setResult(parseWalletImport(v, today));
    } catch (e) {
      setResult(null);
      setError(e instanceof Error ? e.message : "That couldn't be read.");
    }
  };

  const save = async () => {
    if (!result) return;
    if (replace) {
      for (const r of existing.programs) await remove('programs', r.id);
      for (const r of existing.cards) await remove('cards', r.id);
      for (const r of existing.perks) await remove('perks', r.id);
      for (const r of existing.redemptions) await remove('redemptions', r.id);
    }
    for (const r of result.programs) await put('programs', r);
    for (const r of result.cards) await put('cards', r);
    for (const r of result.perks) await put('perks', r);
    for (const r of result.redemptions) await put('redemptions', r);
    toast('Wallet imported');
    navigate('/wallet', { replace: true });
  };

  const count = result
    ? [
        [result.programs.length, 'points balance', 'points balances'],
        [result.cards.length, 'card', 'cards'],
        [result.perks.length, 'perk or bonus', 'perks and bonuses'],
        [result.redemptions.length, 'redemption', 'redemptions'],
      ]
        .filter(([n]) => (n as number) > 0)
        .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`)
        .join(', ')
    : '';

  return (
    <Page title="Import from Claude" backTo="/settings">
      <div className={s.importSteps}>
        <p>
          1. Copy this request and send it to Claude in claude.ai, where your points history lives.
        </p>
        <Button
          icon="copy-simple"
          onClick={async () => {
            const ok = await copyText(WALLET_PROMPT);
            toast(ok ? 'Copied' : "Couldn't copy. Select the text below instead.");
          }}
        >
          Copy request
        </Button>
        <p>2. Paste Claude's answer here.</p>
        <TextArea label="Claude's answer" value={text} onChange={read} minRows={6} />
        {error && <p className={s.importError}>{error}</p>}
        {result && (
          <>
            <p className="muted">
              Found {count || 'nothing to import'}.
              {result.skipped.length > 0 && ` Skipped: ${result.skipped.join(', ')}.`}
            </p>
            <Toggle
              label="Replace what's in the wallet"
              detail="Off adds these to what's there"
              checked={replace}
              onChange={setReplace}
            />
            <Button variant="primary" onClick={() => void save()} disabled={!count}>
              Import
            </Button>
          </>
        )}
      </div>
    </Page>
  );
}
